/**
 * AssetManifest — The ONE place that knows about meshes and animation
 * clips on disk.
 *
 * # Why this file exists
 *
 * Coach-authored scene data (Scene/Actor/Keyframe) only ever references
 * football concepts: position roles ("OL_LT", "WR_X", "QB"…),
 * assignment verbs ("pass_block", "slant", "drop_5"), and intensity
 * tags ("jog", "sprint"). It must NEVER reference a GLB filename or
 * an animation clip name — otherwise every asset upgrade would force
 * a scene-data migration.
 *
 * The manifest is the translation layer:
 *
 *     scene.actor.role  → manifest.ROLES[role].mesh    → MESHES[id].glb
 *     keyframe.verb     → manifest.VERBS[verb].clip    → CLIPS[id].glb
 *     scene.team        → manifest.TEAMS[team_id]      (or uniform_overrides)
 *
 * Swapping the X-Bot mesh for the €19 Synty football pack later is a
 * change to ONE line in `MESHES` (or even just the GLB binary file on
 * disk). Zero scene migrations, zero coach-visible change beyond the
 * better visuals.
 *
 * # Files this manifest references
 *
 * All assets are served from `/scenario-os/domains/football/assets/three/`
 * — owned by the football domain pack, served by domain_assets_routes.py.
 * The CSP allows blob: URLs because GLTFLoader fetches embedded textures
 * via blob URLs (see deployments/docker/nginx*.conf).
 *
 *   xbot_mesh.glb               Mixamo X-Bot character (T-pose, ~1.8MB)
 *   humanoid_base.glb           Legacy Vanguard mesh (~410KB) — fallback
 *   anim_idle.glb               Standing idle (looped)
 *   anim_football_stance.glb    Pre-snap 2-point stance (looped)
 *   anim_football_hike.glb      Center snap action (one-shot)
 *   anim_quarterback_pass.glb   QB throwing motion (one-shot)
 *   anim_football_catch.glb     Catch (one-shot)
 *   anim_receiver_catch.glb     WR-style catch (one-shot)
 *   anim_run.glb                Jog/run (looped, in-place)
 *   anim_sprint.glb             Sprint (looped, in-place)
 *   anim_backpedal.glb          Walking backwards (looped, in-place)
 *   anim_strafe_left.glb        Side-shuffle left (looped, in-place)
 *   anim_strafe_right.glb       Side-shuffle right (looped, in-place)
 */

// ─── BONE-NAME REMAP — Studio Ochi → Mixamo ──────────────────────────────────

/**
 * Studio Ochi Football meshes (ochi_man_a/b/c) ship with a Blender Rigify
 * "Metarig" skeleton that uses dot-suffix naming. Our 11 anim_*.glb
 * animation clips were authored for Mixamo X-Bot (mixamorig*) so
 * ClipLoader.loadClipForMesh feeds this map to
 * THREE.SkeletonUtils.retargetClip to remap track names at load time.
 *
 * Map convention: { mixamorigBoneName: ochiBoneName }. ClipLoader
 * inverts internally to { targetBoneName: sourceBoneName } as
 * SkeletonUtils.retargetClip's `options.names` expects.
 *
 * Bones omitted from the map (fingers, breast.L/R, pelvis.L/R,
 * *_end markers, heel.02.*) have no Mixamo equivalent and stay
 * un-driven — invisible at the LOD we render players at.
 *
 * `mixamorigNeck` and `mixamorigHead` map to `spine.004` /
 * `spine.005` because Ochi's Rigify rig doesn't have a separate
 * neck/head — the upper spine chain handles both. Visual result:
 * head moves with the upper spine instead of independently — fine
 * for football where the camera is far enough that head twist
 * isn't visible.
 *
 * Source: docs/OCHI_INSPECTION.md, generated by
 * scripts/ochi_inspect_glb.py.
 */
/**
 * Ochi GLBs ship 6–7 native animation clips authored against the
 * Ochi Rigify rig. These are MUCH more natural-looking than retargeted
 * Mixamo clips because the artist-authored hand placement, finger
 * curl, foot pivot, etc. is preserved exactly — retargeting only
 * carries skeletal rotation, not those subtleties.
 *
 * `native_clip_aliases` maps short manifest verb-clip ids
 * (`football_stance`, `idle`, `sprint`, etc.) to substring needles
 * the loader uses to look up the matching Ochi clip name. The
 * full Ochi clip names look like
 * "Metarig Man.013|Metarig Man.013|American Football Hold" — a
 * prefix-doubled artefact of Blender 4.2's NLA-strip glTF exporter
 * (see scripts/blender_ochi_fbx_to_glb.py). Substring match keeps
 * the manifest readable.
 *
 * When PlayerModel.ensureClipAction sees a verb whose alias is
 * present in the mesh's embedded clip list, it builds the
 * AnimationAction from THAT clip directly (no Mixamo retarget
 * round-trip — saves the retarget cost AND gets a hand-authored
 * pose). Verbs without an alias fall through to ClipLoader's Mixamo
 * retarget path as before.
 *
 * Coverage today (per docs/OCHI_INSPECTION.md):
 *   football_stance / idle  → "Hold"
 *   run / sprint / walk / jog / press_release / slant / hitch /
 *      out / in / go / post / corner / curl / comeback / carry /
 *      rush / blitz / scramble / break  → "Run Fast"
 *   pass_throw / quarterback_pass        → "Throw 01"
 *   catch / receiver_catch / football_catch → "Catch and Fall"
 *   football_hike → "Kickoff"  (closest behavioural match — the
 *      hand-down trigger animation; not perfect but better than a
 *      Mixamo retargeted hike that arrives in the wrong skeleton)
 *
 * Verbs without coverage (backpedal, strafe_left, strafe_right,
 * pass_block when run_block fails, etc.) still use Mixamo retarget.
 */
/**
 * Alias values may be EITHER:
 *   - a string substring (legacy form) → use the matched native clip
 *     directly with default looping behavior, OR
 *   - an object `{ clip, loop?, time?, paused? }` (extended form) →
 *     apply `time_seconds` and `paused` to the AnimationAction after
 *     mounting. This is the only way today to play a hand-authored
 *     Ochi clip "frozen at frame 0" — useful because Ochi only ships
 *     ONE stance clip ("Hold" = 3-point lineman crouch). We use the
 *     frame-0 bind pose of that same clip as a safe upright pose for
 *     skill / QB roles. See OCHI_INSPECTION.md for clip inventory.
 *
 * `_ensureNativeClipAction` understands both forms; `applyAliasOpts`
 * (in PlayerModel) honors the time/paused fields after action.play().
 */
export const OCHI_NATIVE_CLIP_ALIASES = {
  // OL/DL — freeze the Hold clip at the deepest 3-point crouch frame
  // and pause it. Without `paused:true` this clip plays its full 4.17s
  // motion loop and the linemen visibly oscillate up→down→up forever
  // (coach-reported "they keep going down to stance and getting up
  // through the whole play"). The Ochi Hold clip's spine bone hits
  // its lowest Y position at t=1.625s (verified by inspecting the GLB
  // animation channels) — that's the deepest 3-point crouch in the
  // 4-second cycle. Freezing here renders OL/DL as statues in 3-point
  // stance. Same alias is reused by pass_block / run_block /
  // cover_zone — those verbs all share clip='football_stance' so they
  // inherit the frozen pose, which is what we want for held states
  // (a coach diagram of a play is mostly held shapes between discrete
  // events, not continuous motion).
  football_stance: { clip: 'Hold', loop: true, time: 1.625, paused: true },
  idle:            { clip: 'Hold', loop: true, time: 0.0, paused: true },
  // Skill positions (WR/RB/TE/LB/DB/CB/S/NB) — keep the native Hold
  // clip but freeze at frame 0 (the in-between standing pose before
  // the crouch keyframe). Reads as upright with hands at sides.
  pre_snap_stance_upright: { clip: 'Hold', loop: true, time: 0.0, paused: true },
  // QB — freeze the Throw 01 clip at frame 0 (the in-between draw-back
  // pose before the throwing arm cocks). Reads as upright with hands
  // forward at chest height — an under-center / shotgun-ready pose.
  pre_snap_stance_qb: { clip: 'Throw 01', loop: true, time: 0.0, paused: true },
  // ── Locomotion clips — these MUST keep looping. A receiver running
  //    a slant should look like he's moving (legs cycling) while his
  //    actor.position translates along the route waypoints. Freezing
  //    these would defeat the purpose. We only freeze HELD poses.
  run:              'Run Fast',
  sprint:           'Run Fast',
  // ── One-shot clips — start at frame 0, play through to the end,
  //    crossfade back to default_idle (handled by playManifestClip's
  //    one_shot_ms timer). No alias-level freeze.
  quarterback_pass: 'Throw 01',
  football_catch:   'Catch and Fall',
  receiver_catch:   'Catch and Fall',
  football_hike:    'Kickoff',
};

export const OCHI_TO_MIXAMO_BONE_MAP = {
  mixamorigHips:           'spine',
  mixamorigSpine:          'spine.001',
  mixamorigSpine1:         'spine.002',
  mixamorigSpine2:         'spine.003',
  mixamorigNeck:           'spine.004',
  mixamorigHead:           'spine.005',
  mixamorigLeftShoulder:   'shoulder.L',
  mixamorigLeftArm:        'upper_arm.L',
  mixamorigLeftForeArm:    'forearm.L',
  mixamorigLeftHand:       'hand.L',
  mixamorigRightShoulder:  'shoulder.R',
  mixamorigRightArm:       'upper_arm.R',
  mixamorigRightForeArm:   'forearm.R',
  mixamorigRightHand:      'hand.R',
  mixamorigLeftUpLeg:      'thigh.L',
  mixamorigLeftLeg:        'shin.L',
  mixamorigLeftFoot:       'foot.L',
  mixamorigLeftToeBase:    'toe.L',
  mixamorigRightUpLeg:     'thigh.R',
  mixamorigRightLeg:       'shin.R',
  mixamorigRightFoot:      'foot.R',
  mixamorigRightToeBase:   'toe.R',
};

// ─── MESHES ──────────────────────────────────────────────────────────────────

/**
 * Each mesh entry describes a humanoid GLB the renderer can instantiate.
 * `helmet_offset` and `pads_offset` are local-space positions relative to
 * the head bone / chest bone where our procedural football gear (helmet,
 * shoulder pads — see FootballGear.js) is attached. These vary per mesh
 * because head/chest bone positions differ slightly between rigs.
 *
 * Ochi meshes set both offsets to all-zeros because Ochi wears its own
 * moulded helmet + pads in the mesh — PlayerModel suppresses the
 * FootballGear procedural attach pass when both offsets are zero.
 */
export const MESHES = {
  xbot: {
    glb: 'xbot_mesh.glb',
    scale: 1.0,
    // Mixamo X-Bot rig — clean athletic mannequin (T-pose). Bone naming
    // is the standard `mixamorig*` set so all our existing stance code
    // and helmet/pads attachment works unchanged.
    helmet_offset: [0, 0.06, 0.02],
    pads_offset: [0, 0.12, 0.0],
    // X-Bot ships unrigged textures as a uniform-mask material — letting
    // tintHumanoidUniform() recolour just the body shell per team.
    has_uniform_mask: false,
    label: 'Mixamo X-Bot',
    // ── Forward-axis offset (radians) ────────────────────────────────
    // The renderer's "facing" convention is +X = downfield (the way
    // offense faces pre-snap). Different exports of the same Mixamo
    // skeleton land with different bind-pose forward axes:
    //   * Vanguard mesh exported via meshopt-pipe ships facing +X,
    //     so its offset is 0.
    //   * X-Bot mesh as we exported it via Blender ships facing +Z
    //     (pointing toward the camera in default Three coords), so
    //     setting rotation.y = 0 actually puts the player facing the
    //     end zone perpendicular to where we wanted. The fix is to
    //     ADD this offset to every player's rotation.y in
    //     FootballScene3D.setActors() / updateFrame().
    // Coaches reported "players facing different sidelines, none of
    // them facing the right way" — root cause was this. Easy to
    // re-tune per mesh as we add the Synty pack later.
    forward_axis_offset_y: -Math.PI / 2,
  },
  vanguard: {
    glb: 'humanoid_base.glb',
    scale: 1.0,
    helmet_offset: [0, 0.06, 0.02],
    pads_offset: [0, 0.12, 0.0],
    has_uniform_mask: false,
    label: 'Mixamo Vanguard (legacy fallback)',
    forward_axis_offset_y: 0,
  },

  // ── Synty POLYGON American Football pack ──────────────────────────
  // These three entries register the per-archetype Synty meshes the
  // pipeline produces (scripts/synty_integrate.sh →
  // scripts/blender_synty_reparent.py). Each Synty FBX is re-parented
  // in Blender to the X-Bot armature with automatic-weights so it
  // ships with the EXACT mixamorig* skeleton our existing 11
  // anim_*.glb clips drive — i.e. zero retargeting at runtime.
  //
  // GLBs are produced by the Blender step at
  //   /opt/mixamo_assets/synty/glb/synty_<archetype>.glb
  // and copied into core/scenario/domain_packs/football/content/assets/three/
  // by the integrate script.
  //
  // If a synty_*.glb is NOT present on disk, HumanoidLoader falls
  // back to FALLBACK_MESH_ID (vanguard) automatically — see _load()
  // in HumanoidLoader.js — so registering them here BEFORE the user
  // uploads is safe. No runtime crash, just a degraded mesh until
  // the integrate script drops the GLBs in place.
  //
  // ── forward_axis_offset_y ─────────────────────────────────────────
  // Synty meshes in the POLYGON pack ship facing +Z when exported
  // from their default scene. After re-parenting to X-Bot the
  // forward axis SHOULD match X-Bot's (-π/2) but Blender's exporter
  // is finicky about pivot orientation. We start at 0 and let the
  // user tune empirically — open the editor 3D view, eyeball whether
  // the offence faces downfield, bump by ±π/2 until correct. The
  // tuning step is documented in docs/SYNTY_INTEGRATION.md.
  //
  // ── bone_name_remap (OPTIONAL — backup path) ──────────────────────
  // When Blender's automatic-weights re-parenting produces visually
  // broken output (twisted limbs, missing weights) for a particular
  // Synty character, the fallback is to ship the Synty NATIVE
  // skeleton in the GLB and add a Synty→Mixamo bone-name map here.
  // ClipLoader.loadClipForMesh() then runs
  //   THREE.SkeletonUtils.retargetClip(clipRoot, srcRoot, clip, opts)
  // with `opts.names` built from this map, producing a one-time
  // cached AnimationClip whose track names address the Synty bones
  // instead of mixamorig*.
  //
  // The map is { mixamorigBoneName: syntyBoneName }. Synty's
  // POLYGON characters use a fairly consistent naming convention:
  //
  //   Mixamo                       Synty POLYGON (typical)
  //   ─────────────────────────    ────────────────────────
  //   mixamorigHips                root  / pelvis / Hips
  //   mixamorigSpine               spine_01
  //   mixamorigSpine1              spine_02
  //   mixamorigSpine2              spine_03
  //   mixamorigNeck                neck_01
  //   mixamorigHead                head
  //   mixamorigLeftShoulder        clavicle_l
  //   mixamorigLeftArm             upperarm_l
  //   mixamorigLeftForeArm         lowerarm_l / forearm_l
  //   mixamorigLeftHand            hand_l
  //   mixamorigRightShoulder       clavicle_r
  //   mixamorigRightArm            upperarm_r
  //   mixamorigRightForeArm        lowerarm_r / forearm_r
  //   mixamorigRightHand           hand_r
  //   mixamorigLeftUpLeg           thigh_l
  //   mixamorigLeftLeg             calf_l
  //   mixamorigLeftFoot            foot_l
  //   mixamorigRightUpLeg          thigh_r
  //   mixamorigRightLeg            calf_r
  //   mixamorigRightFoot           foot_r
  //
  // These are STARTING POINTS — the actual Synty bone names depend on
  // which exact character the user buys. Print the bone names from
  // Blender (`bpy.context.object.pose.bones`) once after import and
  // refine. See docs/SYNTY_INTEGRATION.md for the manual workflow.
  //
  // Leave bone_name_remap UNDEFINED in the happy path — when it's
  // absent ClipLoader.loadClipForMesh() falls straight through to
  // loadClip() with zero overhead.
  // ── Studio Ochi Low-Poly American Football pack ───────────────────
  // The synty_* entries above were placeholders for a Synty POLYGON
  // pipeline that re-parented the mesh to the Mixamo X-Bot armature
  // in Blender (so no runtime retargeting was needed). We never
  // shipped those GLBs — instead we bought the Studio Ochi pack
  // (€30, gs://flowgenix-platform-artifacts/studio-ochi-football/)
  // which already comes rigged + animated to a Blender Rigify
  // "Metarig" skeleton and skips the Blender re-parent step.
  //
  // Conversion pipeline lives at
  //   scripts/blender_ochi_fbx_to_glb.py     (FBX → GLB on dev-1)
  //   scripts/ochi_inspect_glb.py            (skeleton/anim dump)
  // Inspection results:                       docs/OCHI_INSPECTION.md
  //
  // Each Ochi GLB ships:
  //   * 1 SkinnedMesh ('AmericanFootballMan.00X')
  //   * 1 armature ('Metarig Man.00X') with 58 joints
  //   * 6–7 native animations baked in: "Run Fast", "Hold",
  //     "Throw 01", "Catch and Fall", "Kick", "Kickoff" (all 4.17s,
  //     authored against the Ochi rig — much more natural than
  //     retargeted Mixamo would be for those verbs)
  //
  // Because Ochi's skeleton is NOT mixamorig*, the existing 11
  // anim_*.glb Mixamo clips need to be retargeted at runtime. The
  // `bone_name_remap` field below feeds ClipLoader.loadClipForMesh
  // → THREE.SkeletonUtils.retargetClip — see ClipLoader.js for the
  // caching strategy (one retargeted clip per (clipId, meshId), kept
  // forever).
  //
  // Map convention: { mixamorigBoneName: ochiBoneName }. Bones
  // omitted from the map (fingers, breast.L/R, pelvis.L/R, *_end
  // markers, heel.02.*) have no Mixamo equivalent and remain
  // un-driven during playback — visually invisible at the LOD we
  // render players at.
  //
  // forward_axis_offset_y: starts at 0 because Blender 4.2's glTF
  // exporter writes the mesh y-up with neutral facing. The Mixamo
  // X-Bot needed `-Math.PI/2` because its bind pose was rotated;
  // Ochi's bind pose is upright facing -Z (the glTF default), so a
  // small offset of +π/2 (90° clockwise from above) lines its
  // forward axis up with the renderer's "+X = downfield" convention.
  // Tune via `python3 scripts/playwright_synty_verify.py` (works
  // for Ochi too — same /assets/three endpoint).
  //
  // helmet_offset / pads_offset: Ochi already wears a moulded helmet
  // + shoulder pads in the mesh, so FootballGear.js doesn't need to
  // attach procedural ones. We zero those offsets to suppress the
  // FootballGear pass for Ochi meshes (PlayerModel checks for an
  // all-zero helmet_offset → skip).
  ochi_man_a: {
    glb: 'ochi_man_a.glb',
    scale: 1.0,
    helmet_offset: [0, 0, 0],   // Ochi wears its own helmet — skip procedural
    pads_offset:   [0, 0, 0],   // Ochi wears its own shoulder pads — skip procedural
    has_uniform_mask: false,
    label: 'Studio Ochi — Man A (linemen — OL/DL)',
    forward_axis_offset_y: Math.PI / 2,
    archetype_hint: 'lineman',
    bone_name_remap: OCHI_TO_MIXAMO_BONE_MAP,
    native_clip_aliases: OCHI_NATIVE_CLIP_ALIASES,
  },
  ochi_man_b: {
    glb: 'ochi_man_b.glb',
    scale: 1.0,
    helmet_offset: [0, 0, 0],
    pads_offset:   [0, 0, 0],
    has_uniform_mask: false,
    label: 'Studio Ochi — Man B (skill — WR/RB/TE/CB/S/LB)',
    forward_axis_offset_y: Math.PI / 2,
    archetype_hint: 'skill',
    bone_name_remap: OCHI_TO_MIXAMO_BONE_MAP,
    native_clip_aliases: OCHI_NATIVE_CLIP_ALIASES,
  },
  ochi_man_c: {
    glb: 'ochi_man_c.glb',
    scale: 1.0,
    helmet_offset: [0, 0, 0],
    pads_offset:   [0, 0, 0],
    has_uniform_mask: false,
    label: 'Studio Ochi — Man C (quarterback)',
    forward_axis_offset_y: Math.PI / 2,
    archetype_hint: 'qb',
    bone_name_remap: OCHI_TO_MIXAMO_BONE_MAP,
    native_clip_aliases: OCHI_NATIVE_CLIP_ALIASES,
  },
};

/**
 * Reference mapping for the bone_name_remap fallback path. NOT consumed
 * by the renderer directly — copy this object into a MESHES entry's
 * `bone_name_remap` field when you need to retarget Synty native
 * skeletons. Keys are the Mixamo bone names our anim_*.glb clips drive;
 * values are the Synty POLYGON equivalents.
 */
export const SYNTY_TO_MIXAMO_BONE_MAP_TEMPLATE = {
  mixamorigHips:           'pelvis',
  mixamorigSpine:          'spine_01',
  mixamorigSpine1:         'spine_02',
  mixamorigSpine2:         'spine_03',
  mixamorigNeck:           'neck_01',
  mixamorigHead:           'head',
  mixamorigLeftShoulder:   'clavicle_l',
  mixamorigLeftArm:        'upperarm_l',
  mixamorigLeftForeArm:    'lowerarm_l',
  mixamorigLeftHand:       'hand_l',
  mixamorigRightShoulder:  'clavicle_r',
  mixamorigRightArm:       'upperarm_r',
  mixamorigRightForeArm:   'lowerarm_r',
  mixamorigRightHand:      'hand_r',
  mixamorigLeftUpLeg:      'thigh_l',
  mixamorigLeftLeg:        'calf_l',
  mixamorigLeftFoot:       'foot_l',
  mixamorigRightUpLeg:     'thigh_r',
  mixamorigRightLeg:       'calf_r',
  mixamorigRightFoot:      'foot_r',
};

export const DEFAULT_MESH_ID = 'xbot';
// FALLBACK_MESH_ID is what HumanoidLoader serves when the requested
// mesh GLB is missing on disk (e.g. synty_qb.glb 404 because the user
// hasn't uploaded the Synty QB FBX yet). We point this at xbot — our
// most reliable rigged + animated baseline — so any unmapped or
// not-yet-shipped mesh degrades to a clean Mixamo X-Bot rather than
// disappearing or rendering the legacy Vanguard. Vanguard remains in
// MESHES as a deeper fallback option for emergencies / old scenes.
export const FALLBACK_MESH_ID = 'xbot';

// ─── CLIPS ───────────────────────────────────────────────────────────────────

/**
 * Each clip entry describes one animation clip GLB on disk. Clips are
 * loaded lazily by ClipLoader.loadClip(clipId) and cached forever after
 * first resolve. The renderer only loads what's actually needed for the
 * scene currently in view (pass_block scenes don't pay for the catch
 * clip download).
 *
 * `loop`: true = THREE.LoopRepeat, false = THREE.LoopOnce + clamp.
 * `time_scale`: optional playback speed multiplier (1.0 = native).
 * `clip_name`: optional — Mixamo exports the clip with the FBX file
 *              name; left null we just take the first AnimationClip in
 *              the GLB's animations[] array.
 */
export const CLIPS = {
  idle:            { glb: 'anim_idle.glb',            loop: true,  time_scale: 1.0 },
  football_stance: { glb: 'anim_football_stance.glb', loop: true,  time_scale: 1.0 },
  // Per-archetype pre-snap variants. On Ochi meshes these resolve
  // via OCHI_NATIVE_CLIP_ALIASES to frozen-frame native clips
  // (no GLB fetch). On X-Bot / Vanguard fallback meshes they fall
  // through to the standard Idle clip — combined with PlayerModel's
  // bone-pose ready stance, that produces a reasonable upright /
  // QB-ready silhouette for non-Ochi assets without needing
  // additional anim_*.glb files. See PlayerModel.applyReadyStance.
  pre_snap_stance_upright: { glb: 'anim_idle.glb',    loop: true,  time_scale: 1.0 },
  pre_snap_stance_qb:      { glb: 'anim_idle.glb',    loop: true,  time_scale: 1.0 },
  football_hike:   { glb: 'anim_football_hike.glb',   loop: false, time_scale: 1.0 },
  quarterback_pass:{ glb: 'anim_quarterback_pass.glb',loop: false, time_scale: 1.0 },
  football_catch:  { glb: 'anim_football_catch.glb',  loop: false, time_scale: 1.0 },
  receiver_catch:  { glb: 'anim_receiver_catch.glb',  loop: false, time_scale: 1.0 },
  run:             { glb: 'anim_run.glb',             loop: true,  time_scale: 1.0 },
  sprint:          { glb: 'anim_sprint.glb',          loop: true,  time_scale: 1.0 },
  backpedal:       { glb: 'anim_backpedal.glb',       loop: true,  time_scale: 1.0 },
  strafe_left:     { glb: 'anim_strafe_left.glb',     loop: true,  time_scale: 1.0 },
  strafe_right:    { glb: 'anim_strafe_right.glb',    loop: true,  time_scale: 1.0 },
};

// ─── ROLES ───────────────────────────────────────────────────────────────────

/**
 * role → which mesh + which pre-snap stance + which idle clip.
 *
 * `pre_snap_stance` is the verb the renderer applies before the snap.
 * `default_idle` is what plays when the actor has no assignment.
 *
 * When a role doesn't appear here we fall back to GENERIC_ROLE.
 */
/**
 * Every role gets `pre_snap_stance: 'football_stance'` because EVERY
 * football player on the LOS at the snap is in some kind of football
 * stance — the linemen in 3-point, the receivers in a 2-point release
 * stance, the LBs in a balanced ready, the DBs in a backpedal-ready,
 * the QB in an under-center / shotgun crouch.
 *
 * Coaches reported (rightly) that the previous behaviour — only OL/DL
 * got the football_stance, every "skill" position got Mixamo's
 * Standing Idle which is just "person standing with arms at their
 * sides" — looked wrong. "Players standing aimlessly" is exactly what
 * Standing Idle conveys.
 *
 * `default_idle` is what plays when the play isn't running and no
 * assignment verb is set — we keep it on `football_stance` too so the
 * pre-snap pose persists between keyframes during scene authoring.
 *
 * The QB is the only role that uses the bare `idle` for default_idle
 * because the football_stance loop has the QB hunched forward like a
 * lineman, which doesn't read as "QB ready to take the snap." Coaches
 * scrubbing through play timelines need the QB visually distinct.
 */
// ── Mesh routing (role → mesh_id) ──────────────────────────────────
//
// Roles route to the Studio Ochi per-archetype meshes shipped in
// `core/scenario/domain_packs/football/content/assets/three/ochi_man_*.glb`.
// When an ochi_*.glb GLB is missing on disk (e.g. partial deploy,
// CDN cache miss, etc.) HumanoidLoader._load catches the 404 and
// falls back to FALLBACK_MESH_ID (xbot) without crashing the
// renderer — see HumanoidLoader.js.
//
// Practical fallback chain for an OL actor on a fresh checkout:
//   role.mesh = 'ochi_man_a'
//     → ochi_man_a.glb 404  (asset not deployed yet)
//     → xbot              (Mixamo X-Bot, ~1.8MB baseline)
//
// To temporarily force xbot for any role (e.g. while debugging an
// Ochi-specific rendering issue), change `mesh:` to 'xbot' here —
// no other code change needed.
//
// The synty_* placeholder MESHES entries upstream are kept around in
// case we ever revive that pipeline; nothing references them today.
// Per-archetype pre-snap stance routing (coach feedback: "the QB and
// the running back are on the same stance as the offensive line.
// each player should be doing different things based on their role").
//
//   - football_stance         → 3-point / lineman crouch (Ochi: full
//                                "Hold" loop). For OL + DL roles only.
//   - pre_snap_stance_upright → upright at-attention pose for skill
//                                positions (WR/RB/TE/CB/S/LB/NB).
//                                On Ochi: "Hold" frozen at frame 0.
//   - pre_snap_stance_qb      → upright with hands forward at chest
//                                height, under-center / shotgun ready.
//                                On Ochi: "Throw 01" frozen at frame 0.
export const ROLES = {
  offensive_line:    { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },
  center:            { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },
  guard:             { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },
  tackle:            { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },
  defensive_line:    { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },
  defensive_tackle:  { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },
  defensive_end:     { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },
  nose_tackle:       { mesh: 'ochi_man_a', pre_snap_stance: 'football_stance',         default_idle: 'football_stance',         archetype_hint: 'lineman' },

  quarterback:       { mesh: 'ochi_man_c', pre_snap_stance: 'pre_snap_stance_qb',      default_idle: 'pre_snap_stance_qb',      archetype_hint: 'qb' },
  running_back:      { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  wide_receiver:     { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  tight_end:         { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  fullback:          { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  cornerback:        { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  safety:            { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  linebacker:        { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  middle_linebacker: { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  outside_linebacker:{ mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
  nickel_back:       { mesh: 'ochi_man_b', pre_snap_stance: 'pre_snap_stance_upright', default_idle: 'pre_snap_stance_upright', archetype_hint: 'skill' },
};

export const GENERIC_ROLE = {
  mesh: 'ochi_man_b',
  pre_snap_stance: 'pre_snap_stance_upright',
  default_idle: 'pre_snap_stance_upright',
  archetype_hint: 'skill',
};

// ─── VERBS — Coach Authoring vocabulary ──────────────────────────────────────

/**
 * The "verb library" that the coach picks from in the assignment dropdown.
 *
 * Each verb maps to:
 *   - clip: which animation clip to play (must be a key in CLIPS)
 *   - locks_position: true = actor stays put while the verb plays
 *                     (block, throw, catch). false = actor translates
 *                     along its waypoints while the clip plays in-place.
 *   - one_shot_ms: optional — duration after which to crossfade back to
 *                  the role's default_idle. If unset, the clip loops or
 *                  plays through naturally.
 *   - locomotion_axis: 'forward' | 'backward' | 'lateral' — used by the
 *                      locomotion clip selector to pick run/backpedal/strafe
 *                      based on movement vector direction.
 *
 * IMPORTANT: This list is the canonical verb dictionary. The coach UI
 * (assignment dropdown) and the AI suggest agent both consume the keys.
 * Adding a new verb is one entry here + (optionally) a new clip GLB.
 */
export const VERBS = {
  // ── Offensive line ──
  pass_block: {
    clip: 'football_stance', loop: true, locks_position: true,
    label: 'Pass block', category: 'block',
  },
  run_block: {
    clip: 'football_stance', loop: true, locks_position: false,
    label: 'Run block', category: 'block',
  },
  pull: {
    clip: 'run', loop: true, locks_position: false, locomotion_axis: 'lateral',
    label: 'Pull (left/right)', category: 'block',
  },

  // ── Quarterback ──
  hike: {
    clip: 'football_hike', loop: false, locks_position: true, one_shot_ms: 600,
    label: 'Snap (hike)', category: 'qb',
  },
  dropback_3: {
    clip: 'backpedal', loop: true, locks_position: false, locomotion_axis: 'backward',
    label: 'Drop 3-step', category: 'qb',
  },
  dropback_5: {
    clip: 'backpedal', loop: true, locks_position: false, locomotion_axis: 'backward',
    label: 'Drop 5-step', category: 'qb',
  },
  dropback_7: {
    clip: 'backpedal', loop: true, locks_position: false, locomotion_axis: 'backward',
    label: 'Drop 7-step', category: 'qb',
  },
  pass_throw: {
    clip: 'quarterback_pass', loop: false, locks_position: true, one_shot_ms: 900,
    label: 'Throw pass', category: 'qb',
  },
  scramble: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Scramble', category: 'qb',
  },

  // ── Receivers / TE / RB (route running) ──
  // The "route" verbs all use the locomotion clip; the route SHAPE is
  // expressed as waypoints in the annotation. The renderer translates
  // the actor along the waypoints while the clip plays in-place.
  press_release: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Press release', category: 'route',
  },
  slant: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Slant', category: 'route',
  },
  hitch: {
    clip: 'run', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Hitch', category: 'route',
  },
  out: {
    clip: 'run', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Out', category: 'route',
  },
  in: {
    clip: 'run', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'In (dig)', category: 'route',
  },
  go: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Go (fly)', category: 'route',
  },
  post: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Post', category: 'route',
  },
  corner: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Corner', category: 'route',
  },
  comeback: {
    clip: 'run', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Comeback', category: 'route',
  },
  curl: {
    clip: 'run', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Curl', category: 'route',
  },
  catch: {
    clip: 'receiver_catch', loop: false, locks_position: true, one_shot_ms: 700,
    label: 'Catch', category: 'route',
  },

  // ── Running back specific ──
  carry: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Carry the ball', category: 'rb',
  },
  block_back: {
    clip: 'football_stance', loop: true, locks_position: true,
    label: 'Stay in to block', category: 'rb',
  },

  // ── Defensive line / linebackers ──
  rush: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Pass rush', category: 'd_line',
  },
  contain: {
    clip: 'football_stance', loop: true, locks_position: false,
    label: 'Contain edge', category: 'd_line',
  },
  blitz: {
    clip: 'sprint', loop: true, locks_position: false, locomotion_axis: 'forward',
    label: 'Blitz', category: 'lb',
  },

  // ── Coverage (LB/DB) ──
  cover_man: {
    clip: 'backpedal', loop: true, locks_position: false, locomotion_axis: 'backward',
    label: 'Man coverage', category: 'coverage',
  },
  cover_zone: {
    clip: 'football_stance', loop: true, locks_position: false,
    label: 'Zone drop', category: 'coverage',
  },
  press: {
    clip: 'football_stance', loop: true, locks_position: true,
    label: 'Press at LOS', category: 'coverage',
  },
  read_qb: {
    clip: 'football_stance', loop: true, locks_position: false,
    label: 'Read QB', category: 'coverage',
  },

  // ── Locomotion-as-verb (when the coach just wants the player to move) ──
  walk:        { clip: 'run',          loop: true, locks_position: false, locomotion_axis: 'forward',  label: 'Walk',           category: 'move', time_scale: 0.5 },
  jog:         { clip: 'run',          loop: true, locks_position: false, locomotion_axis: 'forward',  label: 'Jog',            category: 'move' },
  sprint:      { clip: 'sprint',       loop: true, locks_position: false, locomotion_axis: 'forward',  label: 'Sprint',         category: 'move' },
  backpedal:   { clip: 'backpedal',    loop: true, locks_position: false, locomotion_axis: 'backward', label: 'Backpedal',      category: 'move' },
  shuffle_left:  { clip: 'strafe_left',  loop: true, locks_position: false, locomotion_axis: 'lateral', label: 'Shuffle left',  category: 'move' },
  shuffle_right: { clip: 'strafe_right', loop: true, locks_position: false, locomotion_axis: 'lateral', label: 'Shuffle right', category: 'move' },
  idle:        { clip: 'idle',         loop: true, locks_position: true,                                 label: 'Idle',           category: 'move' },
  break:       { clip: 'sprint',       loop: true, locks_position: false, locomotion_axis: 'forward',  label: 'Break',          category: 'move', time_scale: 1.3 },
};

// Verb categories for grouping in the UI dropdown
export const VERB_CATEGORIES = [
  { id: 'block',    label: 'Blocking' },
  { id: 'qb',       label: 'Quarterback' },
  { id: 'route',    label: 'Routes (WR/TE)' },
  { id: 'rb',       label: 'Running back' },
  { id: 'd_line',   label: 'Pass rush / D-line' },
  { id: 'lb',       label: 'Linebacker' },
  { id: 'coverage', label: 'Coverage (DB/LB)' },
  { id: 'move',     label: 'Movement' },
];

// ─── INTENSITY ───────────────────────────────────────────────────────────────

/**
 * Intensity modifies the playback speed of locomotion clips and which
 * locomotion clip is preferred when the coach hasn't specified one.
 * Coaches set this per route segment in the designer (walk/jog/sprint/break).
 */
export const INTENSITIES = {
  walk:   { time_scale: 0.55, prefer_clip: 'run' },
  jog:    { time_scale: 0.85, prefer_clip: 'run' },
  sprint: { time_scale: 1.20, prefer_clip: 'sprint' },
  break:  { time_scale: 1.40, prefer_clip: 'sprint' },
};

export const DEFAULT_INTENSITY = 'jog';

// ─── TEAMS — uniform palette defaults ────────────────────────────────────────

/**
 * Default uniform kits keyed by team_id. Per-scene overrides via
 * Scene.uniform_overrides[team] always win — these are the fallbacks when
 * the scene doesn't carry overrides.
 *
 * Coaches edit these once per team in the Coach > Settings > Team Uniforms
 * editor; the values get persisted into the team document and consumed
 * by SceneCanvas + FootballScene3D + the SVG playbook diagram.
 */
export const TEAMS = {
  home: {
    primary: '#1E3A8A', helmet: '#1E3A8A', pads: '#1E3A8A',
    accent: '#FFFFFF',  facemask: '#222222',
    label: 'Home (default blue)',
  },
  away: {
    primary: '#B91C1C', helmet: '#B91C1C', pads: '#B91C1C',
    accent: '#FFFFFF',  facemask: '#222222',
    label: 'Away (default red)',
  },
};

// ─── Resolution helpers (the only API the renderer uses) ─────────────────────

export function resolveRole(roleId) {
  if (!roleId) return GENERIC_ROLE;
  return ROLES[String(roleId).toLowerCase()] || GENERIC_ROLE;
}

export function resolveMesh(meshId) {
  return MESHES[meshId] || MESHES[FALLBACK_MESH_ID];
}

/**
 * Forward-axis correction offset for a given mesh id. Add this to a
 * player's rotation.y AFTER computing the desired facing in the
 * scene's "+X = downfield" convention. Returns 0 for any mesh that
 * already uses the convention natively.
 */
export function meshFacingOffset(meshId) {
  const m = resolveMesh(meshId);
  return (m && typeof m.forward_axis_offset_y === 'number') ? m.forward_axis_offset_y : 0;
}

export function resolveMeshForRole(roleId) {
  const role = resolveRole(roleId);
  return resolveMesh(role.mesh);
}

export function resolveVerb(verbId) {
  if (!verbId) return null;
  return VERBS[String(verbId).toLowerCase()] || null;
}

export function resolveClip(clipId) {
  if (!clipId) return null;
  return CLIPS[String(clipId).toLowerCase()] || null;
}

export function resolveIntensity(intensityId) {
  if (!intensityId) return INTENSITIES[DEFAULT_INTENSITY];
  return INTENSITIES[String(intensityId).toLowerCase()] || INTENSITIES[DEFAULT_INTENSITY];
}

/**
 * Pick the best locomotion clip for an actor based on their movement
 * vector and intensity. Falls back to the role's default_idle if the
 * actor isn't moving.
 *
 * @param {object} opts
 * @param {THREE.Vector3} opts.velocity   — world-space velocity
 * @param {number} opts.facingY           — world-space facing yaw (radians)
 * @param {string} opts.intensity         — coach-authored intensity tag
 * @returns {string} clip id
 */
export function pickLocomotionClip({ velocity, facingY, intensity }) {
  const i = resolveIntensity(intensity);
  if (!velocity || velocity.lengthSq() < 0.01) return null; // not moving
  // Decide forward / backward / lateral by projecting velocity onto the
  // actor's facing axis. Threshold avoids jitter at near-zero alignment.
  const fx = Math.cos(facingY || 0);
  const fz = Math.sin(facingY || 0);
  const dot = velocity.x * fx + velocity.z * fz;
  const forwardMag = Math.abs(dot);
  const lateralMag = Math.sqrt(velocity.lengthSq() - dot * dot);
  if (lateralMag > forwardMag * 1.2) {
    // mostly lateral — pick the strafe direction
    const cross = velocity.x * (-fz) + velocity.z * fx;
    return cross > 0 ? 'strafe_right' : 'strafe_left';
  }
  if (dot < -0.2) return 'backpedal';
  return i.prefer_clip;
}

/**
 * The full list of verb ids — used by the AI suggest agent (tells it
 * what verbs it's allowed to emit) and by the coach UI dropdown.
 */
export function listVerbIds() {
  return Object.keys(VERBS);
}

export function listVerbsForCategory(categoryId) {
  return Object.entries(VERBS)
    .filter(([_, v]) => v.category === categoryId)
    .map(([id, v]) => ({ id, ...v }));
}

/**
 * Asset URL builder — matches the domain_assets_routes endpoint
 * (/scenario-os/domains/football/assets/three/{filename}).
 */
const _API_BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SCENARIO_OS_API)
  || '/scenario-os';
export function assetUrl(filename) {
  return `${_API_BASE}/domains/football/assets/three/${filename}`;
}
